import React from 'react';
import { Box, Typography, Rating, Avatar } from '@mui/material';
import { styled } from '@mui/material/styles';
import StarIcon from '@mui/icons-material/Star';

// Editorial theme colors
const COLORS = {
  cream: '#FAF8F5',
  charcoal: '#2D2A26',
  terracotta: '#C45D3A',
  border: '#2D2A26'
};

const SectionContainer = styled(Box)({
  backgroundColor: COLORS.cream,
  borderBottom: `1px solid ${COLORS.charcoal}`
});

const SectionHeader = styled(Box)({
  padding: '48px 64px 32px',
  borderBottom: `1px solid ${COLORS.charcoal}`,
  '@media (max-width: 768px)': {
    padding: '32px 20px 24px'
  }
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: '42px',
  fontWeight: 700,
  color: COLORS.charcoal,
  letterSpacing: '-1px',
  '@media (max-width: 768px)': {
    fontSize: '32px'
  }
});

const SectionSubtitle = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '14px',
  fontWeight: 400,
  color: COLORS.charcoal,
  opacity: 0.6,
  textTransform: 'uppercase',
  letterSpacing: '2px',
  marginTop: '8px'
});

const RatingOverview = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginTop: '16px'
});

const RatingNumber = styled(Typography)({
  fontFamily: "'Space Mono', monospace",
  fontSize: '48px',
  fontWeight: 700,
  color: COLORS.charcoal,
  lineHeight: 1
});

const RatingDetails = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px'
});

const ReviewCount = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '14px',
  fontWeight: 400,
  color: COLORS.charcoal,
  opacity: 0.6
});

const TestimonialsGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  '@media (max-width: 1024px)': {
    gridTemplateColumns: 'repeat(2, 1fr)'
  },
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr'
  }
});

const TestimonialCard = styled(Box)({
  padding: '40px 32px',
  borderRight: `1px solid ${COLORS.charcoal}`,
  borderBottom: `1px solid ${COLORS.charcoal}`,
  transition: 'background-color 0.3s ease',
  '&:hover': {
    backgroundColor: `${COLORS.charcoal}08`
  },
  '&:nth-of-type(3n)': {
    borderRight: 'none',
    '@media (max-width: 1024px)': {
      borderRight: `1px solid ${COLORS.charcoal}`,
      '&:nth-of-type(2n)': {
        borderRight: 'none'
      }
    },
    '@media (max-width: 768px)': {
      borderRight: 'none'
    }
  },
  '@media (max-width: 1024px)': {
    '&:nth-of-type(2n)': {
      borderRight: 'none'
    }
  },
  '@media (max-width: 768px)': {
    borderRight: 'none'
  }
});

const StarRating = styled(Box)({
  display: 'flex',
  gap: '4px',
  marginBottom: '20px'
});

const StarIconStyled = styled(StarIcon)({
  fontSize: '20px',
  color: COLORS.terracotta
});

const ReviewText = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '15px',
  fontWeight: 400,
  color: COLORS.charcoal,
  lineHeight: 1.8,
  marginBottom: '24px',
  fontStyle: 'italic',
  display: '-webkit-box',
  WebkitLineClamp: 4,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden'
});

const ReviewerInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px'
});

const ReviewerAvatar = styled(Avatar)({
  width: '48px',
  height: '48px',
  backgroundColor: COLORS.charcoal,
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: '18px',
  fontWeight: 700,
  borderRadius: 0
});

const ReviewerDetails = styled(Box)({
  flex: 1
});

const ReviewerName = styled(Typography)({
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: '16px',
  fontWeight: 600,
  color: COLORS.charcoal,
  marginBottom: '2px'
});

const ReviewerRole = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '12px',
  fontWeight: 400,
  color: COLORS.charcoal,
  opacity: 0.6,
  textTransform: 'uppercase',
  letterSpacing: '1px'
});

const EditorialTestimonialsSection = ({ testimonials, businessInfo }) => {
  const displayTestimonials = testimonials?.slice(0, 3) || [];

  if (displayTestimonials.length === 0) {
    return null;
  }

  const rating = businessInfo?.rating ||
    (testimonials && testimonials.length > 0
      ? (testimonials.reduce((acc, t) => acc + (t.rating || 5), 0) / testimonials.length).toFixed(1)
      : '5.0');

  const reviewCount = businessInfo?.reviewCount || (testimonials ? testimonials.length : 0);

  return (
    <SectionContainer id="reviews">
      <SectionHeader>
        <SectionSubtitle>What People Say</SectionSubtitle>
        <SectionTitle>Customer Reviews</SectionTitle>

        <RatingOverview>
          <RatingNumber>{rating}</RatingNumber>
          <RatingDetails>
            <Rating
              value={Number(rating)}
              precision={0.1}
              readOnly
              icon={<StarIconStyled />}
              emptyIcon={<StarIcon sx={{ fontSize: '20px', color: `${COLORS.charcoal}30` }} />}
            />
            <ReviewCount>{reviewCount} Reviews</ReviewCount>
          </RatingDetails>
        </RatingOverview>
      </SectionHeader>

      <TestimonialsGrid>
        {displayTestimonials.map((testimonial, index) => (
          <TestimonialCard key={testimonial.id || index}>
            <StarRating>
              {[...Array(testimonial.rating || 5)].map((_, i) => (
                <StarIconStyled key={i} />
              ))}
            </StarRating>

            <ReviewText>
              "{testimonial.review || testimonial.content || 'Great experience!'}"
            </ReviewText>

            <ReviewerInfo>
              <ReviewerAvatar>
                {(testimonial.name || 'C')[0].toUpperCase()}
              </ReviewerAvatar>
              <ReviewerDetails>
                <ReviewerName>
                  {testimonial.name || 'Customer'}
                </ReviewerName>
                <ReviewerRole>
                  {testimonial.role || 'Verified Customer'}
                </ReviewerRole>
              </ReviewerDetails>
            </ReviewerInfo>
          </TestimonialCard>
        ))}
      </TestimonialsGrid>
    </SectionContainer>
  );
};

export default EditorialTestimonialsSection;
